// Layout.js
import { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import MobileSidebar from './MobileSidebar';
import TopBar from './TopBar';
import FloatingActionButton from './FloatingActionButton';
import { NotificationDisplay } from './Notification';
import styled from '@emotion/styled';

const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const MainContent = styled.main`
  flex: 1;
  padding: 40px;
  background-color: #edf0ee;
  min-height: calc(100vh - 60px);
  margin-left: ${props => props.expanded ? '80px' : '300px'};
  margin-top: 60px;
  transition: margin-left 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);

  @media (max-width: 1024px) {
    margin-left: 0;
    padding: 25px;
  }

  @media (max-width: 768px) {
    padding: 20px 15px;
  }

  @media (max-width: 480px) {
    padding: 15px 10px;
    padding-bottom: 80px;
  }
`;

const MobileOverlay = styled.div`
  display: ${props => props.show ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1999;
  opacity: ${props => props.show ? 1 : 0};
  transition: opacity 0.3s ease;

  @media (min-width: 1025px) {
    display: none;
  }
`;

function Layout({ children }) {
  // Начальное состояние - сайдбар развернут (не свернут)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleSidebar = () => {
    if (isMobile) {
      // На мобильных устройствах открываем/закрываем мобильное меню
      setIsMobileMenuOpen(!isMobileMenuOpen);
    } else {
      // На десктопе сворачиваем/разворачиваем сайдбар
      const newState = !isSidebarCollapsed;
      setIsSidebarCollapsed(newState);
      localStorage.setItem('sidebarCollapsed', newState);

      window.dispatchEvent(new CustomEvent('sidebarToggled', {
        detail: { collapsed: newState }
      }));
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Восстанавливаем состояние сайдбара из localStorage при загрузке
  useEffect(() => {
    const collapsed = localStorage.getItem('sidebarCollapsed') === 'true';
    setIsSidebarCollapsed(collapsed);
  }, []);

  useEffect(() => {
    if (isMobile && isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobile, isMobileMenuOpen]);

  return (
    <LayoutContainer>
      <TopBar onMenuClick={toggleSidebar} />
      <NotificationDisplay />

      {/* Десктопный сайдбар */}
      {!isMobile && (
        <Sidebar
          isCollapsed={isSidebarCollapsed}
        />
      )}

      {/* Мобильный сайдбар */}
      {isMobile && (
        <MobileSidebar
          isOpen={isMobileMenuOpen}
          onClose={closeMobileMenu}
        />
      )}

      <MobileOverlay show={isMobileMenuOpen} onClick={closeMobileMenu} />

      <MainContent expanded={isSidebarCollapsed}>
        {children}
      </MainContent>

      {isMobile && <FloatingActionButton />}
    </LayoutContainer>
  );
}

export default Layout;